import React, { useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import moment from 'moment';
import FormModal from '../../../shared/components/custom/FormModal';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';
import EmbedPreview from './EmbedPreview';

const p = partial('NewsForm');
const s = partial('shared');

const shorten = (text, length = 80) =>
  text.length > length ? `${text.slice(0, length - 1)}…` : text;

/**
 * An AI page's versions (`pageId`: its first draft): every time AI wrote
 * or changed it, newest first. Picking one shows it; "Use this version"
 * puts its HTML in the block. Changes made by hand aren't versions.
 */
const AiVersionsModal = ({ isOpen, pageId, onClose, onUse }) => {
  // null while they load
  const [versions, setVersions] = useState(null);
  const [selected, setSelected] = useState(null);
  // The selected version with its HTML
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !pageId) return undefined;
    let current = true;
    setVersions(null);
    setSelected(null);
    setDraft(null);
    setError(null);
    Api.news
      .getAiDraftVersions(pageId)
      .then((list) => {
        if (!current) return;
        setVersions(list);
        setSelected(list[0]?.id || null);
      })
      .catch((err) => {
        if (!current) return;
        setVersions([]);
        setError(String(err));
      });
    return () => {
      current = false;
    };
  }, [isOpen, pageId]);

  useEffect(() => {
    if (!selected) return undefined;
    let current = true;
    setDraft(null);
    Api.news
      .getAiDraft(selected)
      .then((full) => current && setDraft(full))
      .catch((err) => current && setError(String(err)));
    return () => {
      current = false;
    };
  }, [selected]);

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={p('aiVersionsTitle')}
      subtitle={p('aiVersionsHint')}
    >
      <div className="qm-body ai-versions">
        {versions === null && (
          <p className="ai-draft__writing">{p('aiDraftRecentLoading')}</p>
        )}
        {versions?.length === 0 && !error && (
          <p className="ai-draft__writing">{p('aiVersionsEmpty')}</p>
        )}
        {versions?.length > 0 && (
          <div className="ai-versions__layout">
            <ul className="ai-versions__list">
              {versions.map((version, index) => (
                <li key={version.id}>
                  <button
                    type="button"
                    className={`ai-versions__item${
                      version.id === selected
                        ? ' ai-versions__item--active'
                        : ''
                    }`}
                    aria-pressed={version.id === selected}
                    onClick={() => setSelected(version.id)}
                  >
                    <span className="ai-versions__date">
                      {moment(version.createdAt).format('DD.MM.YYYY HH:mm')}
                      {index === 0 && (
                        <span className="ai-draft__badge ml-2">
                          {p('aiVersionsLatest')}
                        </span>
                      )}
                    </span>
                    <span className="ai-versions__what">
                      {version.mode === 'html-edit'
                        ? p('aiVersionsChanged', {
                            change: shorten(version.brief || ''),
                          })
                        : p('aiVersionsWritten')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="ai-draft__preview ai-versions__preview">
              {draft?.html ? (
                <EmbedPreview html={draft.html} fullWidth />
              ) : (
                <p className="ai-draft__writing">{p('aiDraftRecentLoading')}</p>
              )}
            </div>
          </div>
        )}
        {error && (
          <p className="ai-draft__error" role="alert">
            {error}
          </p>
        )}
      </div>
      <div className="qm-footer">
        <Button type="button" outline className="mb-0" onClick={onClose}>
          {s('cancel')}
        </Button>
        <Button
          type="button"
          color="primary"
          className="mb-0"
          disabled={!draft?.html}
          onClick={() => onUse(draft.html)}
        >
          {p('aiVersionsUse')}
        </Button>
      </div>
    </FormModal>
  );
};

export default AiVersionsModal;
